import { ComponentType, FC, lazy } from "react";
import { ModuleSettings } from "../../http/types/modules";
import BeamReinforcement from "./beamReinforcement/BeamReinforcement";
import BeamReinforcementAdminSettings from "./beamReinforcement/BeamReinforcementAdminSettings";
import ResinQuantity from "./concreteResin/ConcreteResin";
import ConcreteResinAdminSettings from "./concreteResin/admin/ConcreteResinAdminSettings";
import DeckDesigner from "./deckDesigner/DeckDesigner";
import DeckDesignerAdminSettings from "./deckDesigner/DeckDesignerAdminSettings";
import EnvelopeDesignerAdminSettings from "./envelopeDesigner/admin/EnvelopeDesignerAdminSettings";
import LockCalculator from "./lockCalculator/LockCalculator";
import LockCalculatorAdminSettings from "./lockCalculator/admin/AdminSettings";
import ScrewConnector from "./screwConnector/ScrewConnector";
import ScrewConnectorAdminSettings from "./screwConnector/ScrewConnectorAdminSettings";

// Loaded on demand, in their own chunks: their consumers render them inside a Suspense.
const UsLiftingCalculator = lazy(() => import("./usLiftingCalculator/UsLiftingCalculator"));
const UsLiftingCalculatorAdminSettings = lazy(
  () => import("./usLiftingCalculator/admin/UsLiftingCalculatorAdminSettings"),
);

// ─── Engine IDs ───
export const CONCRETE_RESIN_ENGINE_ID = "concrete-resin-quantity";
export const DECK_DESIGNER_ENGINE_ID = "deck-designer-terralock";
export const LOCK_CALCULATOR_ENGINE_ID = "lock-calculator";
export const BEAM_REINFORCEMENT_ENGINE_ID = "beam-reinforcement";
export const ENVELOPE_DESIGNER_ENGINE_ID = "envelope-designer";
export const SCREW_CONNECTOR_ENGINE_ID = "screw-shear-connector";
export const US_LIFTING_CALCULATOR_ENGINE_ID = "us-lifting-calculator";

// ─── Engine Config Interface ───
export type EngineConfig = {
  id: string;
  Calculator?: ComponentType<any>;
  AdminSettings?: FC<{ moduleData: ModuleSettings }>;
};

// ─── Engine Registry ───
// Register your new engine here
const engines: Record<string, EngineConfig> = {
  [CONCRETE_RESIN_ENGINE_ID]: {
    id: CONCRETE_RESIN_ENGINE_ID,
    Calculator: ResinQuantity,
    AdminSettings: ConcreteResinAdminSettings,
  },
  [DECK_DESIGNER_ENGINE_ID]: {
    id: DECK_DESIGNER_ENGINE_ID,
    Calculator: DeckDesigner,
    AdminSettings: DeckDesignerAdminSettings,
  },
  [LOCK_CALCULATOR_ENGINE_ID]: {
    id: LOCK_CALCULATOR_ENGINE_ID,
    Calculator: LockCalculator,
    AdminSettings: LockCalculatorAdminSettings,
  },
  [BEAM_REINFORCEMENT_ENGINE_ID]: {
    id: BEAM_REINFORCEMENT_ENGINE_ID,
    Calculator: BeamReinforcement,
    AdminSettings: BeamReinforcementAdminSettings,
  },
  [ENVELOPE_DESIGNER_ENGINE_ID]: {
    id: ENVELOPE_DESIGNER_ENGINE_ID,
    // loaded on demand: its material catalogue and climate data are large
    Calculator: lazy(() => import("./envelopeDesigner/EnvelopeDesigner")),
    AdminSettings: EnvelopeDesignerAdminSettings,
  },
  [SCREW_CONNECTOR_ENGINE_ID]: {
    id: SCREW_CONNECTOR_ENGINE_ID,
    Calculator: ScrewConnector,
    AdminSettings: ScrewConnectorAdminSettings,
  },
  [US_LIFTING_CALCULATOR_ENGINE_ID]: {
    id: US_LIFTING_CALCULATOR_ENGINE_ID,
    Calculator: UsLiftingCalculator,
    AdminSettings: UsLiftingCalculatorAdminSettings,
  },
};

// ─── Accessors ───
export function getEngine(moduleId?: string): EngineConfig | undefined {
  return moduleId ? engines[moduleId] : undefined;
}

// Returns the AdminSettings component for a given engine
export function getEngineAdminSettings(moduleId?: string): FC<{ moduleData: ModuleSettings }> | undefined {
  return getEngine(moduleId)?.AdminSettings;
}
